import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth.store';
import { adminHref } from '../../../lib/adminRoutes';
import { Icon } from '../../../components/ui/Icon';
import { Chip } from '../../../components/ui/Chip';
import { Helper } from '../../../components/ui/Helper';
import { EmptyState } from '../../../components/ui/EmptyState';
import { AnimatedNumber } from '../../../components/ui/AnimatedNumber';
import { SkeletonKPIRow, SkeletonTable } from '../../../components/ui/Skeleton';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { MobileFilters } from '../../../components/ui/MobileFilters';
import {
  OrdinaFiltra, perData, perNumero, perTesto, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina,
} from '../../../components/ui/OrdinaFiltra';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../../lib/utils';
import { isSuperAdmin, payablesQuery, plurale, type MatchKind } from '../adminApi';
import { useAdminActions } from '../useAdminActions';
import { SettlePopover } from '../SettlePopover';
import { MOTIVO_TOLTA_LABEL, TrashPopover } from '../TrashPopover';
import {
  TIPO_SPESA_LABEL, comeAddebitato, daPagareQuery, dataEsatta, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno, metodoDaOperazione, nomeBreve, quando,
  type DaPagare, type GruppoSpesa, type SpesaRow, type SuggerimentoSpesa, type TipoSpesa,
} from '../fatture/fattureApi';

/**
 * ⚠️ LA VISTA NON SI MOSTRA PIÙ dal 26/10/2026 (Lorenzo, "alleggerire"): sul computer Spese
 * usa SpeseDaPagareOra.tsx (da pagare a mano + addebiti da spuntare) e SpeseFisseLoghi.tsx.
 * Da qui si riusano Suggerimento, AzioniSpesa e descrizione; il resto resta com'era, per
 * poterlo rimettere. FattureFornitoriTolte si apre ancora con `?status=tolte`.
 *
 * Spese › Spese da pagare (proposta § 2.3).
 *
 *   frase    quante spese sono scadute e quali in banca risultano già pagate
 *   fascia   Spese scadute · Spese che scadono entro 30 giorni · Pagato ai
 *            fornitori nell'anno
 *   lista    fatture dei fornitori, spese fisse (le occorrenze fino a 30
 *            giorni) e contanti. SENZA tasse (Fisco) e SENZA stipendi, TFR e
 *            compenso (Dipendenti): lo garantisce l'API, /admin/spese/da-pagare.
 *   "Per data / Per fornitore": la stessa lista, raggruppata in un altro modo
 *            (era la tab Fornitori).
 *   movimento in banca  dal 24/9 quello che torna del tutto si segna da solo
 *            dopo il sync Qonto (admin.autochiusura.ts: fatture con importo,
 *            nome e data; spese fisse con il fornitore). Qui restano "In banca
 *            risulta pagata" (basta confermare) e i casi dubbi con il motivo
 *            scritto nel chip: "In banca c'è un addebito di Make di € 9,80
 *            invece di € 9,16: controlla e conferma", "Due addebiti possibili
 *            di HighLevel: scegli quale". Niente più "Probabilmente".
 *   "Segna pagata" (era "Paga": non paga niente, registra).
 *
 * Le fatture dei fornitori tolte dall'elenco si aprono dal menu "Altri elenchi
 * di spese" (`?status=tolte`).
 */
const GRUPPI: Array<{ key: GruppoSpesa; titolo: string }> = [
  { key: 'SCADUTE', titolo: 'Spese scadute' },
  { key: 'PROSSIMI', titolo: 'Spese che scadono entro 30 giorni' },
  { key: 'AVANTI', titolo: 'Spese che scadono più avanti' },
];

const TIPO_TONE: Record<TipoSpesa, 'default' | 'info' | 'ghost'> = { FORNITORE: 'default', SPESA_FISSA: 'info', CONTANTI: 'ghost' };

/**
 * Ordina e filtra (24/9): nel browser, la lista arriva intera. "Scadenza più
 * vicina prima" è la vista di sempre, divisa in scadute / entro 30 giorni /
 * più avanti; con un altro ordine diventa una lista unica.
 */
const ORDINI: OpzioneOrdina<SpesaRow>[] = [
  { key: 'scadenza', label: 'Scadenza più vicina prima', criteri: [perData((r) => r.dueDate), perTesto((r) => r.counterparty)] },
  { key: 'costose', label: 'Prima le spese più costose', criteri: [perNumero((r) => r.amount, 'decrescente'), perData((r) => r.dueDate)] },
  { key: 'economiche', label: 'Prima le spese meno costose', criteri: [perNumero((r) => r.amount), perData((r) => r.dueDate)] },
  { key: 'fornitore', label: 'A chi paghiamo, dalla A alla Z', criteri: [perTesto((r) => r.counterparty), perData((r) => r.dueDate)] },
];

const FILTRI: FiltroDef<SpesaRow>[] = [
  {
    param: 'solo', label: 'Quali spese',
    opzioni: [
      { value: 'scadute', label: 'Solo le spese scadute' },
      { value: 'entro30', label: 'Solo quelle che scadono entro 30 giorni' },
      { value: 'banca', label: 'Solo quelle che in banca risultano già pagate' },
    ],
    test: (r, v) => (v === 'scadute' ? r.gruppo === 'SCADUTE' : v === 'entro30' ? r.gruppo === 'PROSSIMI' : !!r.suggerimento),
  },
  {
    param: 'tipo', label: 'Tipo di spesa', multi: true,
    opzioni: [
      { value: 'FORNITORE', label: 'Fatture dei fornitori' },
      { value: 'SPESA_FISSA', label: 'Spese fisse' },
      { value: 'CONTANTI', label: 'Uscite in contanti' },
    ],
    test: (r, v) => r.tipo === v,
  },
];

const NOTA_CONFERMA = 'Confermata da Spese da pagare: in banca c\'è il pagamento corrispondente.';

/**
 * Il movimento in banca accanto alla riga, con i due campi che l'API aggiunge
 * dal 24/9 (il tipo di base sta in fattureApi.ts, fuori da questa cartella):
 * `dubbio` è il motivo per cui resta a mano, `scegli` vuol dire che ci sono
 * più movimenti possibili e si sceglie da Banca › Movimenti del conto.
 */
export type Movimento = SuggerimentoSpesa & { dubbio?: string | null; scegli?: boolean };
export const movimentoDi = (r: SpesaRow): Movimento | null => (r.suggerimento as Movimento | null) ?? null;

const SOURCE_MATCH: Record<SpesaRow['source'], MatchKind | null> = { PAYABLE: 'PAYABLE_PAYMENT', RECURRING: null, CASH: 'CASH_ENTRY' };

export function SpeseDaPagare({ canWrite, showHistory, onPagate, tabs }: { canWrite: boolean; showHistory?: boolean; onPagate: () => void; tabs: ReactNode }) {
  const [statusParam, setStatus] = useUrlState<string>('status', '');
  if (statusParam === 'tolte') return <FattureFornitoriTolte canWrite={canWrite} onBack={() => setStatus('')} tabs={tabs} />;
  return <DaPagareAperte canWrite={canWrite} showHistory={showHistory} onPagate={onPagate} onTolte={() => setStatus('tolte')} tabs={tabs} />;
}

function DaPagareAperte({ canWrite, showHistory, onPagate, onTolte, tabs }: { canWrite: boolean; showHistory?: boolean; onPagate: () => void; onTolte: () => void; tabs: ReactNode }) {
  const q = useQuery(daPagareQuery(showHistory));
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [per, setPer] = useUrlState<string>('per', '');
  const mobile = useIsMobile();
  // Raggruppare per fornitore sul telefono non serve: con pochi fornitori la
  // lista per data dice già tutto (proposta § 2.3).
  const perFornitore = per === 'fornitore' && !mobile;
  const data = q.data;
  const s = search.trim().toLowerCase();
  const ordina = useOrdina(ORDINI);
  const filtri = useFiltri(FILTRI, data?.items);
  const perScadenza = ordina.key === 'scadenza';
  const items = useMemo(
    () => ordina.applica(filtri.applica((data?.items ?? []).filter((r) => !s || `${r.counterparty} ${r.description ?? ''} ${r.number ?? ''}`.toLowerCase().includes(s)))),
    [data, s, ordina, filtri],
  );
  const ristretta = !!s || filtri.attivi > 0;

  if (q.isLoading && !data) {
    return (
      <>
        <SkeletonKPIRow count={3} withIcon={false} />
        {tabs}
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={6} cols={5} withAvatar={false} /></tbody></table></div>
      </>
    );
  }
  if (!data) return <>{tabs}<EmptyState brand title="Le spese da pagare non si sono caricate" description="Riprova tra qualche secondo." /></>;

  const intestazione = (
    <thead>
      <tr>
        <th>A chi paghiamo</th>
        <th style={{ width: 150 }}>Tipo di spesa</th>
        <th style={{ width: 220 }}>Quando scade</th>
        <th className="num" style={{ width: 130 }}>Importo</th>
        <th style={{ width: 170 }} />
      </tr>
    </thead>
  );

  return (
    <>
      <p className="fs-frase">{frase(data)}</p>
      <Fascia data={data} onPagate={onPagate} />

      {tabs}

      <MobileFilters attivi={(s ? 1 : 0) + filtri.attivi} label="Cerca, ordina e filtra">
        <div className="filterbar filterbar--float mb-4 flex-wrap">
          {!mobile && (
            <>
              <div className="tweak-seg" role="group" aria-label="Come raggruppare le spese">
                <button aria-pressed={!perFornitore} onClick={() => setPer('')}><Icon name="calendar" size={12} /> Per data</button>
                <button aria-pressed={perFornitore} onClick={() => setPer('fornitore')}><Icon name="brief" size={12} /> Per fornitore</button>
              </div>
              <div className="divider" />
            </>
          )}
          <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
            <Icon name="search" size={12} />
            <input aria-label="Cerca fra le spese da pagare" placeholder="Cerca per fornitore, spesa o numero di fattura…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <span className="mute text-[12px]">{items.length === 1 ? '1 spesa da pagare' : `${items.length} spese da pagare`}</span>
          {/* Per fornitore i gruppi hanno il loro ordine (prima chi ha spese
              scadute, poi per totale): lì resta solo Filtra per. */}
          <OrdinaFiltra ordina={perFornitore ? undefined : ordina} filtri={filtri} />
          <ActionsMenu
            variant="default"
            label="Altri elenchi di spese"
            items={[
              { key: 'pagate', label: 'Spese già pagate', icon: 'check', onClick: onPagate },
              { key: 'tolte', label: "Fatture dei fornitori tolte dall'elenco", icon: 'trash', onClick: onTolte },
            ]}
          />
        </div>
      </MobileFilters>

      {items.length === 0 ? (
        ristretta ? (
          <EmptyState brand title="Nessuna spesa da pagare con questi filtri" description="Togli un filtro o prova con il nome del fornitore o della spesa."
            action={<div className="flex gap-2 justify-center">
              {filtri.attivi > 0 && <button className="btn" onClick={filtri.clear}>Togli i filtri</button>}
              {s && <button className="btn" onClick={() => setSearch('')}>Azzera la ricerca</button>}
            </div>} />
        ) : (
          <EmptyState brand title="Nessuna spesa da pagare" description="Fatture dei fornitori, spese fisse e contanti da pagare compaiono qui. Tasse e stipendi hanno le loro pagine." />
        )
      ) : perFornitore ? (
        <PerFornitore items={items} canWrite={canWrite} />
      ) : perScadenza ? (
        GRUPPI.map((g) => {
          const righe = items.filter((r) => r.gruppo === g.key);
          if (!righe.length) return null;
          return (
            <div key={g.key} className="ed-section fs-group">
              <div className="data-hd"><span className="t-micro">{g.titolo}</span></div>
              {/* Scheda sul telefono; la riga non apre un dettaglio (il gesto è
                  Segna pagata, dentro la scheda): `tbl--static`. */}
              <table className="tbl tbl--ed tbl--card tbl--static">
                {intestazione}
                <tbody>
                  {righe.map((r) => <RigaSpesa key={r.key} r={r} canWrite={canWrite} />)}
                </tbody>
              </table>
            </div>
          );
        })
      ) : (
        <div className="ed-section fs-group">
          <div className="data-hd">
            <span className="t-micro">Spese da pagare · {ordina.attiva.label.toLowerCase()}</span>
          </div>
          <table className="tbl tbl--ed tbl--card tbl--static">
            {intestazione}
            <tbody>
              {items.map((r) => <RigaSpesa key={r.key} r={r} canWrite={canWrite} />)}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Frase e fascia
// ════════════════════════════════════════════════════════════════════

function frase(d: DaPagare): ReactNode {
  const scadute = d.items.filter((r) => r.gruppo === 'SCADUTE');
  const prossime = d.items.filter((r) => r.gruppo === 'PROSSIMI');
  // In banca risulta pagata e torna tutto: basta confermare. Quelle che non
  // tornano del tutto hanno il motivo scritto accanto (`dubbio`).
  const pagate = d.items.filter((r) => movimentoDi(r) && !movimentoDi(r)!.dubbio);
  const dubbie = d.items.filter((r) => movimentoDi(r)?.dubbio);
  const parti: ReactNode[] = [];

  if (scadute.length > 0) {
    parti.push(scadute.length === 1
      ? <>Con i dati di oggi <strong>una spesa è scaduta</strong>.</>
      : <>Con i dati di oggi <strong>{scadute.length} spese sono scadute</strong>.</>);
  } else if (prossime.length > 0) {
    parti.push(<>Con i dati di oggi nessuna spesa è scaduta. La prossima è {nomeBreve(prossime[0]!.counterparty)}, {quando(prossime[0]!.dueDate, 'scaduta')}.</>);
  } else {
    parti.push(<>Con i dati di oggi non c'è niente da pagare ai fornitori.</>);
  }
  if (pagate.length === 1) {
    parti.push(<> Una, {nomeBreve(pagate[0]!.counterparty)}, in banca risulta già pagata: confermala.</>);
  } else if (pagate.length > 1) {
    parti.push(<> {pagate.length} in banca risultano già pagate: confermale.</>);
  }
  if (dubbie.length === 1) {
    parti.push(<> Per {nomeBreve(dubbie[0]!.counterparty)} in banca c'è un movimento che non torna del tutto: il motivo è scritto sulla riga.</>);
  } else if (dubbie.length > 1) {
    parti.push(<> Per {dubbie.length} spese in banca c'è un movimento che non torna del tutto: il motivo è scritto sulla riga.</>);
  }
  return <>{parti.map((p, i) => <Fragment key={i}>{p}</Fragment>)}</>;
}

function Fascia({ data, onPagate }: { data: DaPagare; onPagate: () => void }) {
  const t = data.totals;
  const composizione = [
    t.scadute.fatture ? (t.scadute.fatture === 1 ? '1 fattura di fornitore' : `${t.scadute.fatture} fatture di fornitori`) : null,
    t.scadute.fisse ? (t.scadute.fisse === 1 ? '1 spesa fissa' : `${t.scadute.fisse} spese fisse`) : null,
    t.scadute.contanti ? (t.scadute.contanti === 1 ? '1 in contanti' : `${t.scadute.contanti} in contanti`) : null,
  ].filter(Boolean).join(', ');
  return (
    <div className="stat-band fs-fascia">
      <div>
        <div className="st-label">
          Spese scadute
          <Helper text="Fatture dei fornitori, spese fisse e contanti con la scadenza già passata e non segnati come pagati. Le tasse sono nel calendario e nella bacheca dei debiti, gli stipendi in Dipendenti." />
        </div>
        <div className="st-value"><span className="u">€</span><AnimatedNumber value={t.scadute.amount} format={fmtEur} /></div>
        <div className={`st-delta${t.scadute.amount > 0 ? ' text-alert' : ''}`}>{composizione || 'nessuna'}</div>
      </div>
      <div>
        <div className="st-label">Spese che scadono entro 30 giorni</div>
        <div className="st-value"><span className="u">€</span><AnimatedNumber value={t.prossimi.amount} format={fmtEur} /></div>
        <div className="st-delta" title={dataEsatta(data.entro, 'Fino al')}>fino {alGiorno(data.entro)}</div>
      </div>
      <div className="st-click" role="button" tabIndex={0} onClick={onPagate} onKeyDown={(e) => { if (e.key === 'Enter') onPagate(); }} title="Apri le spese già pagate">
        <div className="st-label">
          Pagato ai fornitori nel {t.pagatoAnno.year}
          <Helper text={`Dal 1 gennaio ${t.pagatoAnno.year}, per data di pagamento: fatture dei fornitori segnate pagate, più spese fisse e contanti registrati come pagati. Gli addebiti su carta non registrati qui non ci sono.`} />
        </div>
        <div className="st-value"><span className="u">€</span><AnimatedNumber value={t.pagatoAnno.amount} format={fmtEur} /></div>
        <div className="st-delta">{t.pagatoAnno.count === 1 ? '1 pagamento registrato' : `${t.pagatoAnno.count} pagamenti registrati`}</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Riga (per data)
// ════════════════════════════════════════════════════════════════════

export function descrizione(r: SpesaRow): string | null {
  if (r.tipo === 'FORNITORE') {
    const rata = r.ofTotal && r.ofTotal > 1 && r.seq ? `rata ${r.seq} di ${r.ofTotal} della ` : '';
    const numero = r.number ? `${rata}fattura n. ${r.number}` : rata ? `${rata}fattura` : null;
    return [numero, r.description].filter(Boolean).join(' · ') || null;
  }
  return r.description && r.description !== r.counterparty ? r.description : null;
}

function RigaSpesa({ r, canWrite }: { r: SpesaRow; canWrite: boolean }) {
  const scaduta = r.gruppo === 'SCADUTE';
  return (
    <tr style={{ cursor: 'default' }}>
      <td data-m="hd" className="adm-clip">
        <div className="font-semibold flex items-center gap-2">
          <span className="truncate">{r.counterparty}</span>
          {r.entity === 'PERSONALE_ANDREA' && <Chip tone="ghost">Di Andrea</Chip>}
        </div>
        {descrizione(r) && <div className="text-[12px] mute" title={descrizione(r) ?? undefined}>{descrizione(r)}</div>}
        {r.suggerimento && <Suggerimento r={r} canWrite={canWrite} />}
      </td>
      <td data-m="key" data-l="Tipo di spesa"><Chip tone={TIPO_TONE[r.tipo]}>{TIPO_SPESA_LABEL[r.tipo]}</Chip></td>
      <td data-m="key" data-l="Quando scade" title={dataEsatta(r.dueDate)} style={{ whiteSpace: 'nowrap' }}>
        <span className={scaduta ? 'text-alert font-semibold' : 'mute'}>{quando(r.dueDate, 'scaduta')}</span>
      </td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.amount, true)}</td>
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <AzioniSpesa r={r} canWrite={canWrite} />
      </td>
    </tr>
  );
}

/**
 * Il movimento in banca della riga: un chip che dice cosa c'è e, se qualcosa
 * non torna, perché (scritto dall'API: importo diverso dal solito, più
 * addebiti possibili, più spese fisse dello stesso fornitore…). Accanto,
 * "Conferma il pagamento" o, se i movimenti possibili sono più d'uno,
 * "Scegli il movimento" in Banca.
 */
export function Suggerimento({ r, canWrite }: { r: SpesaRow; canWrite: boolean }) {
  const { confermaProposta, pay } = useAdminActions();
  const navigate = useNavigate();
  const sg = movimentoDi(r)!;
  const dettaglio = `${fmtDate(sg.settledOn)} · € ${fmtEur(sg.amount, true)} · ${sg.counterparty ?? sg.label}`;
  const conferma = () => {
    if (sg.tipo === 'BANCA') {
      confermaProposta.mutate({ lato: 'USCITA', rataId: r.id, txId: sg.txId, paidDate: sg.settledOn ?? r.dueDate ?? '', label: r.counterparty, importo: r.amount, nota: NOTA_CONFERMA });
    } else {
      // Si registra l'importo VERO addebitato (lo fa l'API con il movimento
      // collegato): nel messaggio va quello, non il previsto della scheda.
      pay.mutate({
        source: 'RECURRING', id: r.id, dueDate: r.dueDate, amount: sg.amount, label: r.counterparty,
        paidDate: sg.settledOn ?? r.dueDate ?? '', method: metodoDaOperazione(sg.operationType), bankTransactionId: sg.txId, accountId: null,
      });
    }
  };
  const scegli = () => navigate(adminHref('banca', { vista: 'movimenti', q: sg.counterparty ?? r.counterparty, side: 'DEBIT', unmatched: '1' }));
  return (
    <div className="fs-sugg">
      <Chip tone={sg.dubbio ? 'warn' : 'ok'} dot>
        <span title={dettaglio}>
          {sg.dubbio
            ? sg.dubbio
            : sg.tipo === 'BANCA'
              ? <>In banca risulta pagata {ilGiorno(sg.settledOn)}</>
              : <>In banca risulta pagata {comeAddebitato(sg.operationType)} {ilGiorno(sg.settledOn)}</>}
        </span>
      </Chip>
      {canWrite && (sg.scegli ? (
        <button className="btn btn-sm" onClick={scegli} title="Apri i movimenti del conto di questo fornitore e collega quello giusto">
          <Icon name="link" size={10} /> Scegli il movimento
        </button>
      ) : (
        <button className="btn btn-sm" disabled={confermaProposta.isPending || pay.isPending} onClick={conferma}
          title={`Segna la spesa pagata con il movimento in banca: ${dettaglio}`}>
          <Icon name="check" size={10} /> Conferma il pagamento
        </button>
      ))}
    </div>
  );
}

export function AzioniSpesa({ r, canWrite }: { r: SpesaRow; canWrite: boolean }) {
  const { pay, ignoreDocs } = useAdminActions();
  const navigate = useNavigate();
  const [aperto, setAperto] = useState<null | 'paga' | 'togli'>(null);
  const matchKind = SOURCE_MATCH[r.source];
  const menu: ActionItem[] = [
    // Una spesa fissa si modifica, sospende o termina da Spese fisse: qui si paga soltanto.
    { key: 'fisse', label: 'Apri le spese fisse', icon: 'repeat', onClick: () => navigate(adminHref('spese', { vista: 'fisse' })), hidden: r.source !== 'RECURRING' },
    { key: 'togli', label: "Togli dall'elenco", icon: 'trash', danger: true, onClick: () => setAperto('togli'), hidden: !canWrite || r.source !== 'PAYABLE' },
  ];
  const sottotitolo = r.tipo === 'FORNITORE'
    ? (r.ofTotal && r.ofTotal > 1 && r.seq ? `rata ${r.seq} di ${r.ofTotal}, ${r.counterparty}` : r.counterparty)
    : r.description ?? r.counterparty;
  return (
    <div className="flex justify-end items-center gap-1 relative">
      {canWrite && (
        <button className="btn btn-sm" disabled={pay.isPending} title="Registra il pagamento in Masamune OS: non parte nessun bonifico" onClick={() => setAperto(aperto === 'paga' ? null : 'paga')}>
          <Icon name="check" size={10} /> Segna pagata
        </button>
      )}
      <ActionsMenu items={menu} label={`Azioni su ${sottotitolo}`} />
      {aperto === 'paga' && (
        <SettlePopover
          mode="paga"
          amount={r.amount}
          subtitle={sottotitolo}
          target={matchKind ? { kind: matchKind, id: r.id } : undefined}
          counterparty={r.counterparty}
          side="DEBIT"
          // Solo le fatture dei fornitori stanno su FattureInCloud: le altre
          // spese non creano nessuna differenza da spiegare al commercialista.
          withNote={r.source === 'PAYABLE'}
          pending={pay.isPending}
          onClose={() => setAperto(null)}
          onConfirm={(v) => {
            setAperto(null);
            pay.mutate({ source: r.source, id: r.id, amount: r.amount, label: r.counterparty, dueDate: r.dueDate, ...v });
          }}
        />
      )}
      {aperto === 'togli' && (
        <TrashPopover
          title={r.number ?? r.counterparty}
          pending={ignoreDocs.isPending}
          onClose={() => setAperto(null)}
          onConfirm={(v) => { setAperto(null); ignoreDocs.mutate({ kind: 'payable', ids: [r.refId], label: `${r.counterparty} · ${r.number ?? 's/n'}`, ...v }); }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Per fornitore (era la tab Fornitori): la stessa lista, raggruppata
// ════════════════════════════════════════════════════════════════════

interface Gruppo { chi: string; righe: SpesaRow[]; totale: number; piuVecchia: string | null; scadute: number }

function PerFornitore({ items, canWrite }: { items: SpesaRow[]; canWrite: boolean }) {
  const [aperto, setAperto] = useState<string | null>(null);
  const gruppi = useMemo<Gruppo[]>(() => {
    const m = new Map<string, Gruppo>();
    for (const r of items) {
      const g = m.get(r.counterparty) ?? { chi: r.counterparty, righe: [], totale: 0, piuVecchia: null, scadute: 0 };
      g.righe.push(r);
      g.totale += r.amount;
      if (r.dueDate && (!g.piuVecchia || r.dueDate < g.piuVecchia)) g.piuVecchia = r.dueDate;
      if (r.gruppo === 'SCADUTE') g.scadute++;
      m.set(r.counterparty, g);
    }
    // Chi ha spese scadute prima, poi per importo
    return [...m.values()].sort((a, b) => (b.scadute - a.scadute) || (b.totale - a.totale));
  }, [items]);

  return (
    <div className="ed-section fs-perfornitore">
      <div className="data-hd"><span className="t-micro">Spese da pagare, per fornitore</span></div>
      <table className="tbl tbl--ed">
        <thead>
          <tr>
            <th>Fornitore</th>
            <th className="num" style={{ width: 170 }}>Spese da pagare</th>
            <th style={{ width: 210 }}>La più vecchia scade</th>
            <th className="num" style={{ width: 180 }}>Totale da pagare</th>
            <th style={{ width: 60 }} />
          </tr>
        </thead>
        <tbody>
          {gruppi.map((g) => {
            const open = aperto === g.chi;
            return (
              <Fragment key={g.chi}>
                <tr onClick={() => setAperto(open ? null : g.chi)} aria-expanded={open}>
                  <td>
                    <div className="font-semibold flex items-center gap-2">
                      {g.chi}
                      {g.scadute > 0 && <Chip tone="alert" dot>{g.scadute === 1 ? '1 scaduta' : `${g.scadute} scadute`}</Chip>}
                    </div>
                  </td>
                  <td className="num mono mute">{g.righe.length}</td>
                  <td title={dataEsatta(g.piuVecchia)}><span className={g.scadute ? 'text-alert' : 'mute'}>{quando(g.piuVecchia, 'scaduta')}</span></td>
                  <td className="num mono font-semibold">€ {fmtEur(g.totale, true)}</td>
                  <td><div className="flex justify-end"><Icon name={open ? 'chevronU' : 'chevronD'} size={12} className="mute" /></div></td>
                </tr>
                {open && (
                  <tr className="adm-subrow" style={{ cursor: 'default' }}>
                    <td colSpan={5} style={{ padding: 0 }}>
                      <div className="adm-sublist">
                        {g.righe.map((r) => (
                          <div key={r.key} className="adm-line">
                            <div className="text-[12px]" style={{ width: 150 }} title={dataEsatta(r.dueDate)}>
                              <span className={r.gruppo === 'SCADUTE' ? 'text-alert' : 'mute'}>{quando(r.dueDate, 'scaduta')}</span>
                            </div>
                            <div className="min-w-0 flex-1 truncate">
                              <Chip tone={TIPO_TONE[r.tipo]}>{TIPO_SPESA_LABEL[r.tipo]}</Chip>
                              <span className="mute"> {descrizione(r) ?? ''}</span>
                            </div>
                            <div className="mono font-semibold text-right" style={{ width: 110 }}>€ {fmtEur(r.amount, true)}</div>
                            <div style={{ width: 170 }}><AzioniSpesa r={r} canWrite={canWrite} /></div>
                          </div>
                        ))}
                        {!canWrite && <div className="text-[12px] mute-2 pt-1">Puoi solo guardare: per segnare i pagamenti serve il permesso di scrittura.</div>}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Fatture dei fornitori tolte dall'elenco
// ════════════════════════════════════════════════════════════════════

function FattureFornitoriTolte({ canWrite, onBack, tabs }: { canWrite: boolean; onBack: () => void; tabs: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const superAdmin = isSuperAdmin(user);
  const q = useQuery(payablesQuery({ status: 'cestinate' }));
  const { restoreDocs } = useAdminActions();
  const rows = (q.data ?? []).filter((p) => superAdmin || p.entity !== 'PERSONALE_ANDREA');

  return (
    <>
      {tabs}
      <div className="adm-note">
        <Icon name="trash" size={12} className="mute" />
        <span>
          Qui ci sono le fatture dei fornitori tolte dall'elenco: non entrano nei totali, ma <strong>su FattureInCloud restano</strong>.{' '}
          <a className="underline cursor-pointer" onClick={onBack}>Torna alle spese da pagare</a>
        </span>
      </div>
      {q.isLoading && !q.data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={4} cols={4} withAvatar={false} /></tbody></table></div>
      ) : rows.length === 0 ? (
        <EmptyState brand title="Nessuna fattura di fornitore tolta dall'elenco" description="Qui finiscono le fatture ricevute che nell'elenco non devono stare: i doppioni, gli errori. Si possono sempre rimettere."
          action={<button className="btn" onClick={onBack}><Icon name="arrowLeft" size={12} /> Spese da pagare</button>} />
      ) : (
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Fatture dei fornitori tolte dall'elenco</span>
            <span className="mono mute text-[12px]">{plurale(rows.length, 'fattura', 'fatture')}</span>
          </div>
          <table className="tbl tbl--ed tbl--card tbl--static">
            <thead>
              <tr>
                <th>Fornitore e fattura</th>
                <th style={{ width: 140 }}>Tolta il</th>
                <th>Perché è stata tolta</th>
                <th className="num" style={{ width: 130 }}>Importo</th>
                <th style={{ width: 190 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} style={{ cursor: 'default' }}>
                  <td data-m="hd">
                    <div className="font-semibold">{p.vendorName}</div>
                    <div className="text-[12px] mute">Fattura <span className="mono">{p.number ?? 's/n'}</span></div>
                  </td>
                  <td data-m="key" data-l="Tolta il" className="mono mute">{fmtDate(p.ignoredAt)}</td>
                  <td data-m="row" data-l="Perché è stata tolta">
                    <div>{p.ignoredReason ? MOTIVO_TOLTA_LABEL[p.ignoredReason] : '—'}</div>
                    {p.ignoredNote && <div className="text-[12px] mute">{p.ignoredNote}</div>}
                  </td>
                  <td data-m="key" data-l="Importo" className="num mono font-semibold mute">€ {fmtEur(p.total, true)}</td>
                  <td data-m="row">
                    <div className="flex justify-end">
                      {canWrite && (
                        <button className="btn btn-sm btn-ghost" disabled={restoreDocs.isPending}
                          onClick={() => restoreDocs.mutate({ kind: 'payable', ids: [p.id], label: `${p.vendorName} · ${p.number ?? 's/n'}` })}>
                          <Icon name="undo" size={10} /> Rimetti nell'elenco
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
